import {
    ChevronRight,
    FolderOpen,
    FolderArchive,
    Folder,
    ExternalLink,
    Database,
    User,
    Building2,
    Smartphone,
    History,
    Camera,
    ArrowUpDown,
    Lock,
    Loader2,
} from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AppleStoreIcon } from '@/components/ui/app/icons/apple-store';
import { GooglePlayIcon } from '@/components/ui/app/icons/google-play';
import SearchBar from '@/pages/_partials/SearchBar';
import { QRCodeHorodaty } from '@/components/ui/app/qr-code';
import { useTheme } from '@/lib/contexts/theme-context';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { Skeleton } from '@/components/ui/skeleton';
import { newsData } from '@/lib/utils/news-data';
import { useBalance } from '@/lib/contexts/balance-context';
import { useAuth } from '@/lib/contexts/auth-context';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { useGeolocation } from '@/lib/contexts/device-permissions-context';
import { DialogMobileApp } from '@/pages/_partials/DialogMobileApp';
import SheetPhotoCapture from '@/pages/folders/index/_partials/sheets/SheetPhotoCapture';
import { toast } from 'sonner';
import { DataTable } from '@/components/ui/data-table';
import { useProcedureTemplates } from '@/lib/hooks/use-procedure-templates';
import { useAdminProcedureTemplates } from '@/lib/hooks/use-admin-procedure-templates';
// import { useFavoriteProcedures } from '@/lib/hooks/use-favorite-procedures'; // TODO: réactiver quand prêt sur mobile
import { Button } from '@/components/ui/button';
import { colorWithAlpha } from '@/lib/utils/utils';
import { ItemIcon } from '@/pages/folders/index/_partials/tables/FoldersColumns';
import { useIsMobile } from '@/lib/hooks/use-is-mobile';
import { MobileHeaderHome } from '@/pages/_partials/mobile-headers';
import { useTranslation } from 'react-i18next';

export default function Dashboard() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { balance: tokenBalance, refreshBalance } = useBalance();
    const { user, isLoading: isAuthLoading } = useAuth();
    const { can, canOnSelf } = usePermissions();
    const isMobile = useIsMobile();
    const { theme } = useTheme();
    const { storeLinks } = theme;
    const showQrCode = theme.qrCode?.show;
    const showNews = theme.pages?.dashboard?.news?.show !== false;
    const hasStoreLinks = storeLinks?.appStore || storeLinks?.googlePlay;

    // Blocage pour les contacts d'entreprise sans facture payée
    const billingType = user?.billing_type ?? null;
    const hasInvoicesPaid = Boolean(user?.has_invoices_paid);
    const isContactWithoutInvoice = billingType === 'contact' && !hasInvoicesPaid;
    const isIndividual = user?.role === 'user';
    const hasProcedurePermission = canOnSelf('UPDATE_PROCEDURES') || canOnSelf('UPDATE_PROCEDURE_REPORTS');
    const [recentItems, setRecentItems] = useState([]);
    const [isLoadingFolders, setIsLoadingFolders] = useState(true);
    const [foldersError, setFoldersError] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [isMobileAppModalOpen, setIsMobileAppModalOpen] = useState(false);
    const [isPhotoCaptureOpen, setIsPhotoCaptureOpen] = useState(false);
    const [isPhotoUploading, setIsPhotoUploading] = useState(false);

    const { isEnabled: isGeolocationEnabled, getCurrentPosition } = useGeolocation();

    // Récupération des modèles de procédures depuis l'API
    const { templates: procedureTemplates, isLoading: isLoadingProcedures } = useProcedureTemplates();
    const { templates: adminTemplates, isLoading: isLoadingAdminProcedures } = useAdminProcedureTemplates();
    // const { favoriteIds } = useFavoriteProcedures(); // TODO: réactiver quand prêt sur mobile

    // Combiner mes procédures + procédures-types (sans doublons)
    const allTemplates = useMemo(() => {
        const seen = new Set();
        const combined = [];
        for (const t of [...procedureTemplates, ...adminTemplates]) {
            if (!seen.has(t.uniq_id)) {
                seen.add(t.uniq_id);
                combined.push(t);
            }
        }
        return combined;
    }, [procedureTemplates, adminTemplates]);

    /**
     * Génère une abréviation à partir du nom de la procédure
     */
    const generateAbbreviation = (name) => {
        if (!name) return '??';
        const words = name.split(' ').filter((w) => w.length > 2);
        if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
        }
        return name.substring(0, 2).charAt(0).toUpperCase() + name.substring(1, 2).toLowerCase();
    };

    // Transformer les templates API en format pour l'affichage (max 6)
    const quickProcedures = useMemo(() => {
        const mapTemplate = (template) => ({
            id: template.uniq_id,
            uniq_id: template.uniq_id,
            code: generateAbbreviation(template.name),
            label: template.name,
            color: template.color || '#3B82F6',
            hasFields: template.configuration?.sections?.some((s) => s.fields?.length > 0) ?? false,
        });

        return allTemplates.slice(0, 6).map(mapTemplate);
    }, [allTemplates]);

    const quickLinks = [
        { id: 1, label: t('dashboard:monCompte'), icon: User, path: '/settings/account' },
        { id: 2, label: t('dashboard:gestionDesAppareils'), icon: Smartphone, path: '/settings/devices' },
        ...(!isIndividual
            ? [
                  { id: 3, label: t('dashboard:monEntreprise'), icon: Building2, path: '/settings/company' },
                  { id: 4, label: t('dashboard:historiqueDesActions'), icon: History, path: '/settings/company#actions-history' },
              ]
            : []),
    ];

    const goToFolder = (folderId) => {
        navigate(`/folders/${folderId}`);
    };

    const goToSubfolder = (subfolder) => {
        // Naviguer vers le sous-dossier avec le chemin complet : /folders/{folderUniqId}/subfolders/{uniqId}
        navigate(`/folders/${subfolder.folderUniqId}/subfolders/${subfolder.id}`);
    };

    // Naviguer vers un élément récent (dossier, sous-dossier ou rapport)
    const goToRecentItem = (item) => {
        if (item.type === 'folder') {
            navigate(`/folders/${item.id}`);
        } else if (item.type === 'procedure_report') {
            const procedureId = item.procedureTemplateUniqId || '_';
            navigate(`/procedures/${procedureId}/reports/${item.id}`, {
                state: { returnTo: '/dashboard' },
            });
        } else {
            // Sous-dossier : /folders/{folderUniqId}/subfolders/{uniqId}
            navigate(`/folders/${item.folderUniqId}/subfolders/${item.id}`);
        }
    };

    // Colonnes pour la DataTable des éléments récents (dossiers, sous-dossiers, rapports)
    const recentItemsColumns = useMemo(
        () => [
            {
                accessorKey: 'name',
                header: ({ column }) => (
                    <Button
                        variant="plain"
                        color="neutral"
                        size="auto"
                        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                        className="w-full justify-between! hover:bg-transparent"
                    >
                        {t('dashboard:nom')}
                        <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
                    </Button>
                ),
                cell: ({ row }) => (
                    <div className="flex items-center gap-3">
                        <ItemIcon item={row.original} className="w-9 h-9" />
                        <span className="text-foreground">{row.original.name}</span>
                    </div>
                ),
            },
            {
                accessorKey: 'updatedAt',
                meta: { hideOnMobile: true },
                header: ({ column }) => (
                    <Button
                        variant="plain"
                        color="neutral"
                        size="auto"
                        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                        className="w-full justify-between! hover:bg-transparent"
                    >
                        {t('dashboard:miseAJour')}
                        <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
                    </Button>
                ),
                cell: ({ row }) => (
                    <span className="text-sm text-muted-foreground">{row.original.updatedAt || '—'}</span>
                ),
            },
            {
                id: 'action',
                meta: { showOnMobileOnly: true, className: 'w-9 min-w-9 max-w-9 p-0' },
                header: () => null,
                cell: () => <ChevronRight className="w-5 h-5 text-muted-foreground" />,
            },
        ],
        [t],
    );

    useEffect(() => {
        let isMounted = true;

        // Ne pas charger les dossiers si l'utilisateur n'a pas de facture
        if (isContactWithoutInvoice) {
            setIsLoadingFolders(false);
            setRecentItems([]);
            return () => {
                isMounted = false;
            };
        }

        async function loadRecentItems() {
            setIsLoadingFolders(true);
            setFoldersError('');

            try {
                // Utiliser l'endpoint V2 optimisé pour les éléments récents
                const res = await horodatyApi.getDocumentTreeRecent(6);
                const items = res?.data?.items ?? [];
                console.log({ recentItems: items });

                // Mapper les éléments récents (liste unifiée déjà triée par le backend)
                const mappedItems = items
                    .map((item) => ({
                        id: String(item.uniq_id ?? ''),
                        name: item.name ?? t('dashboard:element'),
                        ref: item.ref ?? null,
                        type: item.type === 'procedure' ? 'procedure_report' : item.type,
                        folderUniqId: item.folder_uniq_id ?? null,
                        parentSubfolderUniqId: item.parent_subfolder_uniq_id ?? null,
                        procedureTemplateUniqId: item.procedure_template_uniq_id ?? null,
                        updatedAt: item.updated_at ? new Date(item.updated_at).toLocaleDateString('fr-FR') : null,
                    }))
                    .filter((item) => item.id);
                if (!isMounted) return;
                setRecentItems(mappedItems);
            } catch (error) {
                if (!isMounted) return;
                const message = error?.response?.data?.message || t('dashboard:impossibleDeChargerLesElementsRecents');
                setFoldersError(message);
                setRecentItems([]);
            } finally {
                if (!isMounted) return;
                setIsLoadingFolders(false);
            }
        }

        loadRecentItems();

        return () => {
            isMounted = false;
        };
    }, [isContactWithoutInvoice, t]);

    useEffect(() => {
        let isMounted = true;

        const q = String(searchQuery ?? '').trim();
        if (q.length < 2) {
            setSearchResults([]);
            setSearchError('');
            setIsSearching(false);
            return () => {
                isMounted = false;
            };
        }

        const timer = setTimeout(async () => {
            setIsSearching(true);
            setSearchError('');

            try {
                const [foldersRes, interventionsRes, picturesRes] = await Promise.allSettled([
                    horodatyApi.managerFolders({ search: q }),
                    horodatyApi.managerInterventions({ search: q }),
                    horodatyApi.managerPictures({ search: q }),
                ]);

                // Folders
                let folderRows = [];
                if (foldersRes.status === 'fulfilled') {
                    const folders = foldersRes.value?.data?.data ?? [];
                    folderRows = (Array.isArray(folders) ? folders : [])
                        .map((f) => ({
                            type: 'folder',
                            id: String(f?.uniq_id ?? f?.id ?? ''),
                            name: f?.name ?? t('dashboard:dossier'),
                        }))
                        .filter((f) => f.id);
                } else {
                    // Fallback for non-manager users
                    try {
                        const res = await horodatyApi.listFolders({ all: 1, search: q });
                        const folders = res?.data?.data ?? [];
                        folderRows = (Array.isArray(folders) ? folders : [])
                            .map((f) => ({
                                type: 'folder',
                                id: String(f?.uniq_id ?? f?.id ?? ''),
                                name: f?.name ?? t('dashboard:dossier'),
                            }))
                            .filter((f) => f.id);
                    } catch {
                        // ignore
                    }
                }

                // Interventions (displayed like folders)
                let interventionRows = [];
                const interventionIdToFolderId = new Map();
                if (interventionsRes.status === 'fulfilled') {
                    const interventions = interventionsRes.value?.data?.data ?? [];
                    interventionRows = (Array.isArray(interventions) ? interventions : [])
                        .map((itv) => {
                            const interventionId = String(itv?.uniq_id ?? itv?.id ?? '');
                            const folderId = itv?.folder_id ?? itv?.folder?.uniq_id ?? null;
                            if (interventionId && folderId) interventionIdToFolderId.set(interventionId, folderId);

                            return {
                                type: 'intervention',
                                id: interventionId,
                                folderId,
                                name: itv?.name ?? t('dashboard:sousDossier'),
                            };
                        })
                        .filter((itv) => itv.id);
                }

                // Pictures
                let pictureRows = [];
                if (picturesRes.status === 'fulfilled') {
                    const pictures = picturesRes.value?.data?.data ?? [];
                    pictureRows = (Array.isArray(pictures) ? pictures : [])
                        .map((pic) => {
                            const pictureId = String(pic?.uniq_id ?? pic?.id ?? '');
                            const interventionId = String(pic?.intervention_id ?? pic?.intervention?.uniq_id ?? '');
                            const folderId = interventionIdToFolderId.get(interventionId) ?? null;

                            return {
                                type: 'picture',
                                id: pictureId,
                                interventionId,
                                folderId,
                                name: pic?.name ?? pic?.file_name ?? t('dashboard:photo'),
                            };
                        })
                        .filter((pic) => pic.id);
                }

                const combined = [...folderRows, ...interventionRows, ...pictureRows];
                const unique = new Map();
                for (const row of combined) {
                    const key = `${row.type}:${row.id}`;
                    if (!unique.has(key)) unique.set(key, row);
                }

                if (!isMounted) return;
                setSearchResults(Array.from(unique.values()).slice(0, 10));
            } catch (error) {
                if (!isMounted) return;
                const message = error?.response?.data?.message || t('dashboard:impossibleDeRechercher');
                setSearchError(message);
                setSearchResults([]);
            } finally {
                if (!isMounted) return;
                setIsSearching(false);
            }
        }, 250);

        return () => {
            isMounted = false;
            clearTimeout(timer);
        };
    }, [searchQuery, t]);

    const formatNewsDate = (epochSeconds) => {
        if (!epochSeconds) return '';
        try {
            return new Date(Number(epochSeconds) * 1000).toLocaleDateString('fr-FR');
        } catch {
            return '';
        }
    };

    return (
        <>
            {/* Mobile Header */}
            <MobileHeaderHome />

            <div className="py-8 pb-[120px] md:pb-8 space-y-6">
                {/* Mobile Only: Prendre une photo + Jetons */}
                <div className="px-8 grid grid-cols-2 gap-4 mobile-only md:hidden">
                    {/* Prendre une photo */}
                    {canOnSelf('CAPTURE_CERTIFIED_PHOTOS') && (
                        <Button
                            variant="solid"
                            color="secondary"
                            size="xxl"
                            onClick={() => setIsPhotoCaptureOpen(true)}
                            id="mobile-dashboard-btn-photo-capture"
                            className="rounded-xl p-4 gap-0 flex flex-col items-center justify-center h-auto"
                        >
                            <div
                                className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mb-2"
                                id="mobile-dashboard-btn-photo-capture-icon"
                            >
                                <Camera className="w-5! h-5! text-primary-foreground" />
                            </div>
                            <span
                                id="mobile-dashboard-btn-photo-capture-text"
                                className="text-sm text-secondary-foreground font-normal text-center"
                            >
                                {t('dashboard:prendre')}
                                <br />
                                {t('dashboard:unePhoto')}
                            </span>
                        </Button>
                    )}

                    {/* Jetons restants - Mobile */}
                    {canOnSelf('VIEW_COMPANY_TOKENS') && (
                        <Button variant="solid" color="dark" asChild>
                            <Link
                                to="/tokens"
                                className="flex flex-col items-center gap-2"
                                id="mobile-dashboard-btn-tokens"
                            >
                                <span className="text-[32px] text-secondary font-semibold">{tokenBalance ?? 0}</span>
                                <span className="text-[13px] text-primary-foreground text-center">{t('dashboard:jetonsRestants')}</span>
                            </Link>
                        </Button>
                    )}
                </div>

                {/* Row 1: Procédures rapides + Dossiers récents */}
                <div
                    className={`px-8 grid grid-cols-1 ${isIndividual || !hasProcedurePermission ? '' : 'lg:grid-cols-[1fr_2fr]'} gap-6`}
                >
                    {/* Quick Procedures Card (masqué pour les Particuliers et sans permission procédure) */}
                    {!isIndividual && hasProcedurePermission && (
                        <div className="bg-card rounded-xl border border-border overflow-hidden">
                            <div className="flex items-center justify-between p-4">
                                <h2 className="text-base text-foreground">{t('dashboard:proceduresRapides')}</h2>
                                {!isContactWithoutInvoice && (
                                    <Button variant="solid" color="primary" asChild>
                                        <Link to="/procedures">
                                            <ExternalLink className="w-4 h-4 md:hidden" />
                                            <span className="hidden md:inline">{t('dashboard:voirTout')}</span>
                                        </Link>
                                    </Button>
                                )}
                            </div>
                            <div className="p-4">
                                {isAuthLoading || isLoadingProcedures || isLoadingAdminProcedures ? (
                                    <div className="flex items-center justify-center py-12">
                                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                                    </div>
                                ) : isContactWithoutInvoice ? (
                                    <div className="flex flex-col items-center justify-center py-8 text-center">
                                        <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mb-4">
                                            <Lock className="w-8 h-8 text-muted-foreground" />
                                        </div>
                                        <p className="text-sm text-muted-foreground max-w-xs">
                                            {t('dashboard:fonctionnaliteIndisponible')}
                                        </p>
                                    </div>
                                ) : quickProcedures.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-8 text-center">
                                        <p className="text-sm text-muted-foreground">{t('dashboard:aucuneProcedureDisponible')}</p>
                                        {canOnSelf('CREATE_PROCEDURES') && (
                                            <Button variant="solid" color="primary" asChild className="mt-4">
                                                <Link to="/procedures/create">{t('dashboard:creerUneProcedure')}</Link>
                                            </Button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                        {quickProcedures.map((proc, index) => (
                                            <Link
                                                key={proc.id}
                                                to={
                                                    proc.hasFields
                                                        ? `/procedures/${proc.uniq_id}/reports/create`
                                                        : `/procedures/${proc.uniq_id}`
                                                }
                                                className={`flex flex-col items-center rounded-lg border border-border hover:shadow-md transition-shadow p-3 cursor-pointer ${index >= 3 ? 'hidden lg:flex' : ''}`}
                                                style={{ backgroundColor: colorWithAlpha(proc.color, '15') }}
                                            >
                                                <div className="w-full flex items-center justify-center rounded-t-lg relative">
                                                    <div
                                                        className="w-9 h-9 rounded-full flex items-center justify-center text-primary-foreground text-sm font-semibold"
                                                        style={{ backgroundColor: proc.color }}
                                                    >
                                                        {proc.code}
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-center h-[48px]">
                                                    <p className="text-xs text-foreground text-center leading-tight text-balance">
                                                        {proc.label}
                                                    </p>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Recent Items Card (Dossiers, Interventions, Rapports) */}
                    <div className="bg-card rounded-xl border border-border overflow-hidden">
                        <div className="flex items-center justify-between p-4">
                            <h2 className="text-base text-foreground">{t('dashboard:dossiersRecents')}</h2>
                            {!isContactWithoutInvoice && (
                                <Button variant="solid" color="primary" asChild>
                                    <Link to="/folders">
                                        <ExternalLink className="w-4 h-4 md:hidden" />
                                        <span className="hidden md:inline">{t('dashboard:voirTout')}</span>
                                    </Link>
                                </Button>
                            )}
                        </div>
                        <div className="px-4 pb-4">
                            {isAuthLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                                </div>
                            ) : isContactWithoutInvoice ? (
                                <div className="flex flex-col items-center justify-center py-8 text-center">
                                    <div className="w-16 h-16 bg-surface-hover rounded-full flex items-center justify-center mb-4">
                                        <Lock className="w-8 h-8 text-muted-foreground" />
                                    </div>
                                    <p className="text-sm text-muted-foreground max-w-xs">
                                        {t('dashboard:fonctionnaliteIndisponible')}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {foldersError && (
                                        <div className="text-sm font-semibold text-destructive px-2 py-2">
                                            {foldersError}
                                        </div>
                                    )}

                                    {isLoadingFolders && (
                                        <div className="space-y-1">
                                            {Array.from({ length: isMobile ? 3 : 6 }).map((_, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex items-center justify-between p-2 rounded-lg"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Skeleton className="w-8 h-8 rounded-full" />
                                                        <Skeleton className="h-4 w-40" />
                                                    </div>
                                                    <Skeleton className="h-4 w-20 rounded" />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {!isLoadingFolders && !foldersError && (
                                        <DataTable
                                            columns={recentItemsColumns}
                                            data={recentItems.slice(0, isMobile ? 3 : 6)}
                                            onRowClick={(item) => goToRecentItem(item)}
                                            emptyMessage={t('dashboard:aucunElementRecent')}
                                            emptyIcon={<Folder className="w-12 h-12 text-muted-foreground" />}
                                            showPagination={false}
                                            className="[&_td]:p-2! [&_td:first-child]:ps-4! [&_td:last-child]:pe-4! [&_td]:text-[13px]"
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Row 2: Certifier + Jetons + Quick Links - Desktop only */}
                <div className="px-8 hidden md:grid md:grid-cols-2 xl:grid-cols-[2fr_auto_auto] gap-5">
                    {/* Commencez à certifier Card */}
                    <div
                        id="dashboard-card-show-stores"
                        className="bg-secondary rounded-xl p-6 flex items-center gap-6 md:col-span-2 xl:col-span-1"
                    >
                        {showQrCode && (
                            <div className="bg-card shadow-md shrink-0">
                                <QRCodeHorodaty size={100} />
                            </div>
                        )}
                        <div className="flex flex-col justify-center text-secondary-foreground">
                            <h3 className="text-xl font-bold mb-1 leading-tight">{t('dashboard:commencezACertifier')}</h3>
                            <p className="text-sm font-medium opacity-70 mb-3">{t('dashboard:telechargezLApplication')}</p>
                            {hasStoreLinks && (
                                <div className="flex gap-2 items-center">
                                    {storeLinks.appStore && (
                                        <a
                                            href={storeLinks.appStore}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="bg-foreground-dark text-background px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:opacity-90 transition-opacity"
                                        >
                                            <AppleStoreIcon className="w-4 h-4" />
                                            {t('dashboard:appStore')}
                                        </a>
                                    )}
                                    {storeLinks.googlePlay && (
                                        <a
                                            href={storeLinks.googlePlay}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="bg-foreground-dark text-background px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:opacity-90 transition-opacity"
                                        >
                                            <GooglePlayIcon className="w-4 h-4" />
                                            {t('dashboard:googlePlay')}
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Jetons Card */}
                    {canOnSelf('VIEW_TOKENS') && (
                        <div className="bg-dark text-dark-foreground rounded-xl border border-border p-5 flex flex-col items-center justify-center text-center">
                            <p className="text-sm mb-2">{t('dashboard:soldeDeJetonsDeLEntreprise')}</p>
                            <p className="text-3xl font-semibold text-secondary mb-2.5">{tokenBalance ?? 0}</p>
                            <Button variant="solid" color="secondary" onClick={() => navigate('/tokens')}>
                                {t('dashboard:commander')}
                            </Button>
                        </div>
                    )}

                    {/* Quick Links Grid */}
                    <div className="grid grid-cols-[200px_200px] gap-x-5 gap-y-3">
                        {quickLinks.map((link) => {
                            const IconComponent = link.icon;
                            const content = (
                                <>
                                    <div className="w-10 h-10 bg-surface rounded-full flex items-center justify-center shrink-0">
                                        <IconComponent className="w-5 h-5 text-primary" />
                                    </div>
                                    <span className="text-[13px] text-foreground flex-1 leading-tight">
                                        {link.label}
                                    </span>
                                    <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                                </>
                            );

                            if (link.disabled) {
                                return (
                                    <div
                                        key={link.id}
                                        className="bg-card rounded-xl border border-border py-4 ps-4 pe-2 flex items-center gap-3 opacity-40 cursor-not-allowed pointer-events-none"
                                    >
                                        {content}
                                    </div>
                                );
                            }

                            return (
                                <Link
                                    key={link.id}
                                    to={link.path}
                                    className="bg-card rounded-xl border border-border py-4 ps-4 pe-2 flex items-center gap-3 hover:bg-surface-hover transition-colors"
                                >
                                    {content}
                                </Link>
                            );
                        })}
                    </div>
                </div>

                {/* Row 3: Actualités & Guides */}
                {showNews && (
                    <section>
                        <div className="flex items-center justify-between mb-4 px-8">
                            <h2 className="text-lg text-foreground">{t('dashboard:actualitesGuides')}</h2>
                            <Button variant="solid" color="primary" onClick={() => navigate('/dashboard/news')}>
                                <ExternalLink className="w-4 h-4 md:hidden" />
                                <span className="hidden md:inline">{t('dashboard:voirTout')}</span>
                            </Button>
                        </div>
                        <div className="flex gap-4 overflow-x-auto pb-2 px-8 xl:grid xl:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] xl:grid-rows-[auto] xl:auto-rows-[0] xl:overflow-hidden xl:pb-0">
                            {newsData.slice(0, 5).map((item, idx) => (
                                <Link
                                    key={idx}
                                    to={`/dashboard/news/${idx}`}
                                    className="bg-card rounded-xl overflow-hidden hover:shadow-lg transition-shadow border border-border shrink-0 w-[280px] xl:w-auto xl:shrink"
                                >
                                    <img src={item.image_URL} alt={item.title} className="w-full h-32 object-cover" />
                                    <div className="p-4 space-y-2">
                                        <div className="text-sm text-foreground line-clamp-1">{item.title}</div>
                                        <p className="text-xs text-muted-foreground line-clamp-2">
                                            {item.description || t('dashboard:decouvrezLesDernieresActualitesEtGuides')}
                                        </p>
                                        <p className="text-xs text-secondary">{t('dashboard:lireLaSuite')}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Mobile Only: Quick Links Grid (after news) */}
                <div className="px-8 grid grid-cols-2 gap-3 mobile-only md:hidden">
                    {quickLinks.slice(0, 2).map((link) => {
                        const IconComponent = link.icon;
                        const content = (
                            <>
                                <div className="w-10 h-10 bg-surface rounded-full flex items-center justify-center">
                                    <IconComponent className="w-5 h-5 text-muted-foreground" />
                                </div>
                                <span className="text-xs font-medium text-foreground text-center leading-tight">
                                    {link.label}
                                </span>
                            </>
                        );

                        if (link.disabled) {
                            return (
                                <div
                                    key={link.id}
                                    className="bg-card rounded-xl border border-border p-4 flex flex-col items-center justify-center gap-2 opacity-40 cursor-not-allowed pointer-events-none"
                                >
                                    {content}
                                </div>
                            );
                        }

                        return (
                            <Link
                                key={link.id}
                                to={link.path}
                                className="bg-card rounded-xl border border-border p-4 flex flex-col items-center justify-center gap-2 hover:bg-surface-hover transition-colors"
                            >
                                {content}
                            </Link>
                        );
                    })}
                    {quickLinks.slice(2, 4).map((link) => {
                        const IconComponent = link.icon;
                        const content = (
                            <>
                                <div className="w-10 h-10 bg-surface rounded-full flex items-center justify-center">
                                    <IconComponent className="w-5 h-5 text-muted-foreground" />
                                </div>
                                <span className="text-xs font-medium text-foreground text-center leading-tight">
                                    {link.label}
                                </span>
                            </>
                        );

                        if (link.disabled) {
                            return (
                                <div
                                    key={link.id}
                                    className="bg-card rounded-xl border border-border p-4 flex flex-col items-center justify-center gap-2 opacity-40 cursor-not-allowed pointer-events-none"
                                >
                                    {content}
                                </div>
                            );
                        }

                        return (
                            <Link
                                key={link.id}
                                to={link.path}
                                className="bg-card rounded-xl border border-border p-4 flex flex-col items-center justify-center gap-2 hover:bg-surface-hover transition-colors"
                            >
                                {content}
                            </Link>
                        );
                    })}
                </div>

                <DialogMobileApp open={isMobileAppModalOpen} onOpenChange={setIsMobileAppModalOpen} />

                <SheetPhotoCapture
                    open={isPhotoCaptureOpen}
                    onClose={() => setIsPhotoCaptureOpen(false)}
                    onCapture={async ({ mediaType = 'photo', base64, blob, fileName, mimeType, durationMs }) => {
                        if (!isGeolocationEnabled) {
                            toast.error(t('dashboard:veuillezActiverLaGeolocalisationPourPrendreUnePh'));
                            return;
                        }

                        setIsPhotoUploading(true);

                        try {
                            const position = await getCurrentPosition();

                            if (!position) {
                                toast.error(t('dashboard:impossibleDeRecupererVotrePosition'));
                                return;
                            }

                            const isVideo = mediaType === 'video';
                            await horodatyApi.createPicture({
                                file_name: fileName,
                                latitude: position.latitude,
                                longitude: position.longitude,
                                ...(isVideo
                                    ? { video: blob, mime_type: mimeType, duration_ms: durationMs }
                                    : { base64 }),
                            });
                            toast.success(
                                isVideo
                                    ? t('dashboard:videoHorodateeAjouteeALaGalerie')
                                    : t('dashboard:photoHorodateeAjouteeALaGalerie'),
                            );

                            setIsPhotoCaptureOpen(false);
                            refreshBalance();
                        } catch (error) {
                            console.error('Erreur upload média:', error);
                            const message = error?.response?.data?.message || t('dashboard:impossibleDAjouterLeMedia');
                            toast.error(message);
                        } finally {
                            setIsPhotoUploading(false);
                        }
                    }}
                    isUploading={isPhotoUploading}
                />
            </div>
        </>
    );
}
